import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";
import { PagesID } from "@utils/utilsConfigurations";
import { ServicesURL } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import { isClassroomActivity } from "@utils/cardActions";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { closeUsersAndGroupsModal } from "../store";
import {
  markGroupEnrollmentRemoved,
  useRemovedGroupIds,
} from "../removedGroupsStore";
import EnrollmentListShell from "../components/EnrollmentListShell";
import EnrolledGroupRow from "../components/EnrolledGroupRow";
import {
  buildConfigFromEnrollment,
  disabledByPriority,
} from "../components/EditEnrollmentModal";
import EnrollLOModal from "@pages/Contents/V2/Corporate/UsersGroups/components/EnrollLOModal";
import { EnrollType } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/types";
import { CatalogEntityType } from "@pages/Contents/V2/Corporate/Catalog/types";
// import SolicitConfirmDialog, {
// SolicitTarget,
// } from "../components/SolicitConfirmDialog";
import { useEnrolledGroupsList } from "../hooks/useEnrolledGroupsList";
import { getEnrolledUsersUrl } from "../hooks/useEnrolledUsersList";
import { EnrolledGroupItem, EnrolledUserItem } from "../types";

const PAGE_SIZE = 10;

interface EnrolledGroupsTabProps {
  corporateId: string | number;
  objectId: number;
  objectType: CatalogEntityType;
  objectTitle?: string;
  activityType?: string;
  onChange?: () => void;
}

const EnrolledGroupsTab: React.FC<EnrolledGroupsTabProps> = ({
  corporateId,
  objectId,
  objectType,
  objectTitle,
  activityType,
  onChange,
}) => {
  const { t } = useTranslation("users-and-groups");
  const { t: tInput } = useTranslation("input");
  const navigate = useNavigate();
  const { mutate: mutateCache } = useSWRConfig();
  const { can } = usePermissions();
  const canUpdateEnrollment = can(PERMISSIONS.enrollment.update);
  // const canSolicit = can(PERMISSIONS.enrollment.reminderMassive);

  const enrollTypeOptions = [
    "top_mandatory",
    "top",
    "pre_auto",
    "pre_bottom",
  ].map((key) => ({
    value: key.toUpperCase(),
    label: String(tInput(`options.enroll_type.${key}`)),
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});
  const [deleteTarget, setDeleteTarget] = useState<EnrolledGroupItem | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState<EnrolledGroupItem | null>(null);
  const [isLoadingEnrollment, setIsLoadingEnrollment] = useState(false);
  const [groupEnrollSource, setGroupEnrollSource] = useState<{
    group: EnrolledGroupItem;
    enrollment: EnrolledUserItem | null;
  } | null>(null);
  // const [solicitTarget, setSolicitTarget] = useState<SolicitTarget | null>(
  // null,
  // );

  const {
    items: fetchedItems,
    totalItems: fetchedTotalItems,
    totalPages: fetchedTotalPages,
    isLoading,
    mutate,
  } = useEnrolledGroupsList({
    corporateId,
    objectId,
    page,
    pageSize,
    search: search || undefined,
    fieldFilters,
  });

  const removedGroupIds = useRemovedGroupIds(corporateId, objectId);
  const items = removedGroupIds.size
    ? fetchedItems.filter((group) => !removedGroupIds.has(group.id))
    : fetchedItems;
  const hiddenCount = fetchedItems.length - items.length;
  const totalItems = Math.max(fetchedTotalItems - hiddenCount, 0);
  const totalPages = hiddenCount
    ? Math.max(Math.ceil(totalItems / pageSize), 1)
    : fetchedTotalPages;

  const enrollmentsUrl = `${ServicesURL.getEnrollment}?corporateId=${corporateId}&objectId=${objectId}`;
  const enrolledUsersUrl = getEnrolledUsersUrl(corporateId, objectId);

  const refresh = () => {
    mutate();
    mutateCache(
      (key) =>
        Array.isArray(key) &&
        (key[0] === enrollmentsUrl || key[0] === enrolledUsersUrl),
      undefined,
      { revalidate: false },
    );
    onChange?.();
  };

  const goToGroupDetail = (group: EnrolledGroupItem) => {
    closeUsersAndGroupsModal();
    navigate(
      `/v2/corporate/${corporateId}/detail/users-groups/${
        group.groupType === "DYNAMIC" ? "dynamic-groups" : "groups"
      }/${group.id}`,
    );
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !canUpdateEnrollment || !deleteTarget.groupType)
      return;
    setIsDeleting(true);
    try {
      const url = `${ServicesURL.postEnrollmentMassive}?corporateId=${corporateId}&groupId=${deleteTarget.id}&groupType=${deleteTarget.groupType}&objectId=${objectId}`;
      await fetcher(url, MethodType.DELETE);
      markGroupEnrollmentRemoved(corporateId, objectId, deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      openToast({
        severity: "success",
        message: String(t("card.delete-confirm.success")),
      });
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("card.delete-confirm.error")),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const openGroupEnrollmentEditor = async () => {
    if (!editTarget || !canUpdateEnrollment || !editTarget.groupType) return;
    setIsLoadingEnrollment(true);
    try {
      const url = `${ServicesURL.getEnrollment}?corporateId=${corporateId}&objectId=${objectId}&massiveGroupId=${editTarget.id}&groupType=${editTarget.groupType}&pageIndex=0&pageSize=20`;
      const res = await fetcher(url, MethodType.GET);
      const record: EnrolledUserItem | null =
        res?.enrollmentResponses?.[0] ?? null;
      setGroupEnrollSource({ group: editTarget, enrollment: record });
      setEditTarget(null);
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("card.edit-group-confirm.fetch-error")),
      });
    } finally {
      setIsLoadingEnrollment(false);
    }
  };

  // const openSolicit = (group: EnrolledGroupItem) => {
  // if (!canSolicit || !group.groupType) return;
  // setSolicitTarget({
  // scope: "group",
  // name: group.groupName || `ID ${group.id}`,
  // corporateId,
  // objectId,
  // objectType,
  // groupId: group.id,
  // groupType: group.groupType,
  // });
  // };

  return (
    <EnrollmentListShell
      search={search}
      onSearchSubmit={(v) => {
        setSearch(v);
        setPage(1);
      }}
      hideSearch
      filtersPageId={PagesID["corporate.catalog.courses.subscribed.groups"]}
      filtersDynamicOptions={{ enrollmentType: enrollTypeOptions }}
      fieldFilters={fieldFilters}
      onApplyFilters={(next) => {
        setFieldFilters(next);
        setPage(1);
      }}
      isLoading={isLoading}
      isEmpty={items.length === 0}
      emptyLabel={String(t("empty.enrolled-groups"))}
      page={page}
      pageSize={pageSize}
      totalItems={totalItems}
      totalPages={totalPages}
      onPageChange={setPage}
      onPageSizeChange={(next) => {
        setPageSize(next);
        setPage(1);
      }}
    >
      {items.map((group) => (
        <EnrolledGroupRow
          key={group.id}
          group={group}
          onGoToGroup={goToGroupDetail}
          onEditEnrollment={(g) => setEditTarget(g)}
          onDeleteEnrollment={(g) => setDeleteTarget(g)}
          // onSolicit={openSolicit}
          // S05: azioni sempre visibili, disabilitate senza permesso.
          disabledActions={{
            edit: !canUpdateEnrollment,
            delete: !canUpdateEnrollment,
            // solicit: !canSolicit,
          }}
        />
      ))}

      <ConfirmDialogCst
        open={!!deleteTarget}
        title={String(t("card.delete-group-confirm.title"))}
        description={String(
          t(
            objectType !== "activity" || isClassroomActivity(activityType)
              ? "card.delete-group-confirm.description-progress"
              : "card.delete-group-confirm.description",
          ),
        )}
        confirmLabel={String(t("card.delete-confirm.confirm"))}
        cancelLabel={String(t("card.delete-confirm.cancel"))}
        confirmVariant="danger"
        isSubmitting={isDeleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmDialogCst
        open={!!editTarget}
        title={String(t("card.edit-group-confirm.title"))}
        description={String(
          t("card.edit-group-confirm.description", {
            groupId: editTarget?.id ?? "-",
          }),
        )}
        confirmLabel={String(t("card.edit-group-confirm.confirm"))}
        cancelLabel={String(t("card.delete-confirm.cancel"))}
        isSubmitting={isLoadingEnrollment}
        submittingLabel={String(t("card.edit-group-confirm.loading"))}
        onClose={() => setEditTarget(null)}
        onConfirm={openGroupEnrollmentEditor}
      />

      <EnrollLOModal
        open={!!groupEnrollSource}
        onClose={() => setGroupEnrollSource(null)}
        subjectType="group"
        subject={
          groupEnrollSource
            ? {
                id: groupEnrollSource.group.id,
                name:
                  groupEnrollSource.group.groupName ||
                  `ID ${groupEnrollSource.group.id}`,
              }
            : null
        }
        groupType={groupEnrollSource?.group.groupType ?? "STATIC"}
        corporateId={String(corporateId)}
        onSuccess={() => {
          refresh();
          setGroupEnrollSource(null);
        }}
        initialStep="config"
        initialSelection={
          groupEnrollSource
            ? [
                {
                  id: objectId,
                  kind: objectType,
                  title: objectTitle ?? "",
                  entity: {},
                },
              ]
            : []
        }
        initialConfig={
          groupEnrollSource?.enrollment
            ? buildConfigFromEnrollment(groupEnrollSource.enrollment)
            : undefined
        }
        extraDisabledEnrollTypes={disabledByPriority(
          (groupEnrollSource?.enrollment?.enrollType ??
            groupEnrollSource?.group.enrollmentType) as EnrollType | undefined,
        )}
      />

      {/* <SolicitConfirmDialog
        target={solicitTarget}
        onClose={() => setSolicitTarget(null)}
      /> */}
    </EnrollmentListShell>
  );
};

export default EnrolledGroupsTab;
